import React, { useEffect, useState, useCallback, useRef } from 'react';
import cx from 'classnames';

import styles from './index.module.scss';

// $docs-nav-height (96px) + sticky header height (36px / 3.6rem).
// A heading's bottom edge must clear this zone before the sticky bar
// shows the same endpoint name.
const STICKY_THRESHOLD = 132;

interface Props {
  scrollContainer: HTMLElement | null;
}

const StickyEndpointHeader: React.FC<Props> = ({ scrollContainer }) => {
  const [activeEndpoint, setActiveEndpoint] = useState<string | null>(null);
  const [activeElement, setActiveElement] = useState<HTMLElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const anchorLockUntilRef = useRef<number>(0);

  const handleScroll = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      // After an anchor click, let the click handler's value stick
      // until the scroll settles.
      if (Date.now() < anchorLockUntilRef.current) return;

      if (!scrollContainer) return;

      const headings = scrollContainer.querySelectorAll<HTMLElement>(
        '[data-endpoint-heading="true"]',
      );

      let current: HTMLElement | null = null;
      let currentIndex = -1;

      for (let i = 0; i < headings.length; i++) {
        const heading = headings[i];
        const rect = heading.getBoundingClientRect();
        // Once a heading is fully above the sticky header zone, it becomes active.
        // Using rect.bottom ensures the heading text is no longer visible before
        // the sticky header shows the same endpoint name.
        if (rect.bottom <= STICKY_THRESHOLD) {
          current = heading;
          currentIndex = i;
        } else {
          break;
        }
      }

      if (current) {
        const code = current.querySelector('code');
        const text = code?.textContent ?? current.textContent ?? '';
        if (text) {
          // For endpoint sections (long content), hide the sticky bar
          // when the next heading is visible in the viewport so there's
          // a clean gap before it naturally takes over. Skip this for
          // webhook/method sections — their content is short enough
          // that the next heading is always nearby, which would make
          // the bar flicker constantly.
          let shouldHide = false;
          if (text.startsWith('/') && currentIndex + 1 < headings.length) {
            const nextRect = headings[currentIndex + 1].getBoundingClientRect();
            shouldHide = nextRect.top < window.innerHeight * 0.5;
          }

          if (shouldHide) {
            setActiveEndpoint(null);
            setActiveElement(null);
          } else {
            setActiveEndpoint(text);
            setActiveElement(current);
          }
        } else {
          setActiveEndpoint(null);
          setActiveElement(null);
        }
      } else {
        setActiveEndpoint(null);
        setActiveElement(null);
      }
    });
  }, [scrollContainer]);

  // Hide the sticky header during anchor navigation so the user
  // doesn't see the endpoint name twice (once in the heading the
  // browser just scrolled to, and once in the sticky bar). The lock
  // also prevents the scroll handler from briefly flashing the
  // *previous* endpoint while the scroll animation is in flight.
  const hideForAnchorNav = useCallback(() => {
    setActiveEndpoint(null);
    setActiveElement(null);
    anchorLockUntilRef.current = Date.now() + 500;
  }, []);

  // Intercept clicks on anchor links so the sticky header hides
  // while the browser scrolls to the target heading.
  const handleAnchorClick = useCallback(
    (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest?.('a[href*="#"]');
      if (!anchor) return;

      if ((anchor as HTMLAnchorElement).href?.includes('#')) {
        hideForAnchorNav();
      }
    },
    [hideForAnchorNav],
  );

  useEffect(() => {
    if (!scrollContainer) return;

    scrollContainer.addEventListener('scroll', handleScroll, {
      passive: true,
    });
    document.addEventListener('click', handleAnchorClick);

    handleScroll();

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleAnchorClick);
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [scrollContainer, handleScroll, handleAnchorClick]);

  const handleClick = useCallback(() => {
    if (!activeElement || !scrollContainer) return;

    const anchor = activeElement.closest('a');
    const targetSpan = anchor
      ? anchor.querySelector('[id]')
      : activeElement.querySelector('[id]');

    if (targetSpan) {
      let offsetTop = 0;
      let el = targetSpan as HTMLElement;
      while (el && el !== scrollContainer) {
        if (el.offsetParent === scrollContainer || el.offsetParent === null) {
          offsetTop += el.offsetTop;
          break;
        }
        offsetTop += el.offsetTop;
        el = el.offsetParent as HTMLElement;
      }

      scrollContainer.scrollTo({
        top: offsetTop - 36,
        behavior: 'smooth',
      });
    }
  }, [activeElement, scrollContainer]);

  return (
    <div
      className={cx(
        styles.stickyEndpointHeader,
        activeEndpoint == null && styles.hidden,
      )}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      role='button'
      tabIndex={activeEndpoint == null ? -1 : 0}
      aria-hidden={activeEndpoint == null}
    >
      <code className={styles.endpointPath}>{activeEndpoint}</code>
    </div>
  );
};

StickyEndpointHeader.displayName = 'StickyEndpointHeader';
export default StickyEndpointHeader;
